import { Pipe, PipeTransform, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { emojisService } from '../services/emoji.service';

@Pipe({
  name: 'emojiParser',
  standalone: true,
  pure: true, // pure: true — Angular solo re-ejecuta si cambia algún parámetro
})
export class EmojiParserPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer);
  private emojiService = inject(emojisService);

  private cache = new Map<string, SafeHtml>();

  private static readonly EMOJI_REGEX = /:([a-z0-9_]+):/gi;

  /**
   * Escapa el texto del usuario ANTES de inyectarlo con bypassSecurityTrustHtml.
   *
   * Sin esto, un comentario como `<img src=x onerror=alert(1)>` se ejecutaba en
   * el navegador de todos los lectores (XSS almacenado). El escapado ocurre sobre
   * el texto plano; el HTML de los emojis se genera DESPUÉS, por lo que las
   * etiquetas <img>/<span> que emite este pipe no se ven afectadas.
   */
  private static escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Escapa una URL para poder incrustarla en un atributo HTML sin romperlo.
   */
  private static escapeAttr(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  /**
   * @param text       Texto a procesar
   * @param mode       'image' | 'highlight' (default: 'image')
   * @param isProUser  true si el usuario tiene plan PRO activo
   */
  transform(
    text: string,
    mode: 'image' | 'highlight' = 'image',
    isProUser: boolean = false,
  ): SafeHtml {
    if (!text) return '';

    // La clave incluye isProUser: si cambia, se recalcula el resultado
    const cacheKey = `${mode}:${isProUser}:${text}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const emojiMap = this.emojiService.emojiMap;
    EmojiParserPipe.EMOJI_REGEX.lastIndex = 0;

    // ── PASO 1: escapar HTML del texto del usuario y convertir saltos de línea ─
    // El escapado va PRIMERO: así el contenido del usuario nunca puede inyectar
    // etiquetas. Los \n→<br> se convierten después para no romper los templates
    // <img>/<span> que genera el replace de shortcodes.
    const escaped = EmojiParserPipe.escapeHtml(text).replace(/\n/g, '<br>');

    // ── PASO 2: reemplazar shortcodes por HTML ────────────────────────────────────
    const parsed = escaped.replace(
      EmojiParserPipe.EMOJI_REGEX,
      (match, shortcode) => {
        const url = emojiMap.get(shortcode.toLowerCase());

        // ── Emoji existe en el mapa ─────────────────────────────────────────
        if (url) {
          // La URL viene del backend, pero se escapa antes de meterse en un
          // atributo para que un valor con comillas no rompa el HTML.
          const safeUrl = EmojiParserPipe.escapeAttr(url);

          if (mode === 'highlight') {
            // Modo editor (preview mientras escribes): muestra la imagen del emoji inline.
            // El badge PRO aparece solo si el usuario Free intenta usarlo en su propio texto.
            if (!isProUser) {
              return `<span class="inline-flex items-center gap-0.5 bg-purple-100 dark:bg-purple-900/30 rounded px-1 py-0.5 opacity-70 cursor-default align-middle" title="Emoji PRO — requiere plan PRO para usarlo"><img src="${safeUrl}" alt=":${shortcode}:" loading="lazy" width="20" height="20" class="inline-block h-5 w-5 object-contain"><span class="text-purple-700 dark:text-purple-300 text-[9px] font-bold px-0.5 rounded leading-tight">PRO</span></span>`;
            }
            return `<img src="${safeUrl}" alt=":${shortcode}:" loading="lazy" width="20" height="20" class="inline-block h-5 w-5 object-contain align-middle mx-0.5" title=":${shortcode}:">`;
          }

          // Modo 'image' (vista de comentarios publicados):
          // isProUser aquí es el plan del AUTOR del comentario (no del lector).
          // Si el autor no es PRO, el shortcode se coló manualmente — texto plano.
          // (El backend también lo garantiza: CommentController no guarda
          //  shortcodes de autores no PRO — ver sanitizeCommentContent.)
          if (!isProUser) return match;

          // Autor PRO → renderiza. Cualquier lector (PRO o Free) la ve.
          // ⚠️ Mantener en UNA SOLA LÍNEA para no romper el replace de \n→<br>.
          return `<img src="${safeUrl}" alt=":${shortcode}:" loading="lazy" width="24" height="24" class="inline-block h-6 w-6 object-contain align-middle mx-0.5" title=":${shortcode}:">`;
        }

        // ── Emoji no existe en el mapa ──────────────────────────────────────
        if (mode === 'highlight') {
          return `<span class="inline-flex items-center bg-gray-200 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 font-medium px-1 rounded text-sm align-middle">${match}</span>`;
        }

        // Texto plano sin cambios
        return match;
      },
    );

    const result = this.sanitizer.bypassSecurityTrustHtml(parsed);

    // Evitar que el cache crezca indefinidamente
    if (this.cache.size > 500) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) this.cache.delete(firstKey);
    }
    this.cache.set(cacheKey, result);

    return result;
  }

  /** Limpia el cache — llamar tras crear o eliminar un emoji */
  invalidate(): void {
    this.cache.clear();
  }
}
